import { Injectable, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  GetRowsRequest,
  RestApiService,
} from '@core/services/rest-api.service';
import { DvFormUtils } from '@devinco/ui/legacy';
import { GridApi, IRowNode } from 'ag-grid-community';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import { GridContext } from '@features/vt-grid/interfaces/grid-context';
import { VtSplitterService } from '@shared/services/grid/vt-splitter.service';

@Injectable({ providedIn: 'root' })
export class SplitterWithGridHelperService {
  private restApi = inject(RestApiService);
  private splitterService = inject(VtSplitterService);

  /** NB: Experimental function
   *
   * Only works if data in formGroup matches data in RowNode
   */
  updateGridData(formGroup: FormGroup): void {
    const node = this.splitterService.agGrid.api.getSelectedNodes()[0];
    const values = DvFormUtils.getDirtyValues(formGroup);
    node.updateData({ ...node.data, ...values });
  }

  /** Refresh data for selected row */
  refreshRowData<T>(nodeId: string): Observable<T> {
    const { api, gridOptions } = this.splitterService.agGrid;
    const context: GridContext | undefined = gridOptions?.context;
    const entity = context?.entity;
    const colDefs = api.getColumns()?.map(c => c.getColDef());
    const fields = colDefs?.map(c => c.field) || [];
    const idColDef = colDefs?.find(c => c.type?.includes(ColumnType.Id));
    const idField = idColDef?.field;
    const node = api.getRowNode(nodeId);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const addedData = new BehaviorSubject<any>(undefined);

    if (entity && idField && node) {
      const filterVal = api.getCellValue({ colKey: idField, rowNode: node });
      const filterModel = {
        [idField]: {
          filter: filterVal,
          filterType: typeof filterVal === 'number' ? 'number' : 'text',
          type: 'equals',
        },
      };

      const request: GetRowsRequest = {
        entity,
        fields: fields.filter(field => field) as string[],
        filterModel,
      };

      this.restApi.getData(request).subscribe(res => {
        addedData.next(res.data[0]);

        if (res.totalCount === 1) {
          const rowModelType = api.getGridOption('rowModelType');
          if (rowModelType === 'clientSide') {
            api.applyTransaction({
              update: [{ ...node.data, ...res.data[0] }],
            });
          } else if (rowModelType === 'serverSide') {
            api.applyServerSideTransaction({ update: [res.data[0]] });
          }
        }
      });
    }
    return addedData;
  }

  /** Reload all data in the grid (client-side, tree, or server-side) */
  refreshGrid(): void {
    const { api, gridOptions } = this.splitterService.agGrid;
    const context: GridContext | undefined = gridOptions?.context;
    const rowModelType = api.getGridOption('rowModelType');
    if (rowModelType === 'clientSide') {
      if (context?.treeDataConfig) {
        context.reloadTreeData?.(api, context);
      } else {
        context?.loadClientSideData?.(context.extraFilter);
      }
    } else if (rowModelType === 'serverSide') {
      api.refreshServerSide({ route: [], purge: true });
    }
  }

  /** Remove one or more rows from the grid by node id(s) */
  deleteRow(nodeId: string | string[]): void {
    const { api } = this.splitterService.agGrid;
    const nodeIds = Array.isArray(nodeId) ? nodeId : [nodeId];
    const dataToRemove = nodeIds
      .map(id => api.getRowNode(id)?.data)
      .filter((data): data is NonNullable<typeof data> => !!data);

    if (!dataToRemove.length) return;

    const rowModelType = api.getGridOption('rowModelType');
    if (rowModelType === 'clientSide') {
      api.applyTransaction({ remove: dataToRemove });
    } else if (rowModelType === 'serverSide') {
      api.applyServerSideTransaction({ remove: dataToRemove });
    }
  }

  addNewlyCreatedItemToGrid<TData = any, TMapFnResp = any>(
    entity: string,
    id: number,
    /** used for mutating the getInfo response object */
    newItemMapFn?: (newItem: TData) => TMapFnResp
  ): Observable<IRowNode | undefined> {
    const { api } = this.splitterService.agGrid;
    const colDefs = api.getColumns()?.map(c => c.getColDef()) || [];
    const fields = colDefs.filter(c => c.field).map(c => c.field!);
    const addedNode$ = new ReplaySubject<IRowNode | undefined>(1);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.restApi.getInfo<any>(entity, id.toString(), fields).subscribe(data => {
      const idColDef = colDefs.find(c => c.type?.includes(ColumnType.Id));
      if (idColDef?.field) {
        data[idColDef.field] = id;
      }

      if (newItemMapFn) {
        data = newItemMapFn(data);
      }

      const rowModelType = api.getGridOption('rowModelType');
      let addedNode: IRowNode | undefined;
      if (rowModelType === 'clientSide') {
        addedNode = api.applyTransaction({
          add: [data],
        })?.add[0];
      } else if (rowModelType === 'serverSide') {
        addedNode = api.applyServerSideTransaction({
          add: [data],
          addIndex: 0,
        })?.add?.[0];
      }
      if (addedNode) {
        api.flashCells({
          rowNodes: [addedNode],
        });
        setTimeout(() => {
          api.ensureNodeVisible(addedNode);
          addedNode.setSelected(true);
        });
      }
      addedNode$.next(addedNode);
      addedNode$.complete();
    });

    return addedNode$.asObservable();
  }

  getGridApi(): GridApi {
    return this.splitterService.agGrid.api;
  }
}
